/**
 * WeatherBanner - ESPN-style scrolling ticker for weather alerts
 * Displays at the top of the page with animated text
 */

import { useState, useEffect } from 'react';
import { 
  CloudRain, CloudLightning, Sun, Thermometer, Wind, 
  AlertTriangle, X, CloudSnow, Umbrella, Home
} from 'lucide-react';

const WeatherBanner = ({ alert, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  // Icon mapping based on alert type
  const iconMap = {
    'practice_cancelled': AlertTriangle,
    'indoor_training': Home,
    'heat_advisory': Thermometer,
    'lightning': CloudLightning,
    'rain_delay': CloudRain,
    'wind_advisory': Wind,
    'snow': CloudSnow,
    'custom': Umbrella,
  };

  // Color mapping based on severity
  const severityColors = {
    info: {
      bg: 'from-blue-900/90 to-blue-800/90',
      border: 'border-blue-500',
      text: 'text-blue-300',
      icon: 'text-blue-400',
      glow: 'shadow-blue-500/30',
    },
    warning: {
      bg: 'from-yellow-900/90 to-orange-900/90',
      border: 'border-yellow-500',
      text: 'text-yellow-300',
      icon: 'text-yellow-400',
      glow: 'shadow-yellow-500/30',
    },
    danger: {
      bg: 'from-red-900/90 to-red-800/90',
      border: 'border-red-500',
      text: 'text-red-300',
      icon: 'text-red-400',
      glow: 'shadow-red-500/30',
    },
  };

  const Icon = iconMap[alert.alert_type] || CloudRain;
  const colors = severityColors[alert.severity] || severityColors.warning;

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-r ${colors.bg} border-b-2 ${colors.border} backdrop-blur-sm shadow-lg ${colors.glow}`}
      data-testid="weather-banner"
    >
      {/* Animated background effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Icon and Alert Type Badge */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className={`p-2 rounded-lg bg-black/30 ${colors.icon} animate-pulse`}>
              <Icon className="w-5 h-5" />
            </div>
            <span className={`font-orbitron text-xs uppercase tracking-wider ${colors.text} hidden sm:block`}>
              Weather Alert
            </span>
          </div>
          
          {/* Center: Scrolling Message */}
          <div className="flex-1 overflow-hidden">
            <div className="ticker-wrapper">
              <div className="ticker-content">
                <span className={`font-orbitron text-sm font-bold ${colors.text} mr-8`}>
                  ⚠️ {alert.title}
                </span>
                <span className="font-rajdhani text-sm text-white/90 mr-8">
                  {alert.message}
                </span>
                <span className={`font-orbitron text-sm font-bold ${colors.text} mr-8`}>
                  ⚠️ {alert.title}
                </span>
                <span className="font-rajdhani text-sm text-white/90 mr-8">
                  {alert.message}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right: Dismiss Button */}
          {onDismiss && (
            <button 
              onClick={() => {
                setIsVisible(false);
                onDismiss?.();
              }}
              className="flex-shrink-0 p-1.5 rounded-lg bg-black/30 text-gray-400 hover:text-white hover:bg-black/50 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* CSS for ticker animation */}
      <style>{`
        .ticker-wrapper {
          width: 100%;
          overflow: hidden;
        }
        
        .ticker-content {
          display: inline-flex;
          white-space: nowrap;
          animation: ticker 20s linear infinite;
        }
        
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .ticker-wrapper:hover .ticker-content {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default WeatherBanner;
